import { Badge } from "@/components/ui/badge";

/**
 * StalenessBadge — shows "Xh ago" / "Xd ago" coloured by how stale the
 * underlying data is. Drops into card headers next to titles to make it
 * obvious when an engine has stopped producing fresh rows.
 *
 * Thresholds default to (fresh < 1h, warn 1-6h, stale > 6h, very stale > 24h),
 * which fits crons that fire at least hourly. Cards with slower cadence
 * (e.g. weekly selection) should pass higher thresholds.
 */
export function StalenessBadge({
  ts,
  warnHours = 1,
  staleHours = 6,
  veryStaleHours = 24,
  label,
}: {
  ts: string | number | null | undefined;
  warnHours?: number;
  staleHours?: number;
  veryStaleHours?: number;
  label?: string;
}) {
  if (!ts) {
    return <Badge tone="muted">{label ?? "no data"}</Badge>;
  }
  const dt = typeof ts === "number" ? new Date(ts) : new Date(ts);
  if (isNaN(dt.getTime())) {
    return <Badge tone="muted">{label ?? "no data"}</Badge>;
  }
  const ageMs = Date.now() - dt.getTime();
  const ageH = ageMs / (60 * 60 * 1000);

  let tone: "good" | "warn" | "bad" | "muted" = "good";
  if (ageH > veryStaleHours) tone = "bad";
  else if (ageH > staleHours) tone = "warn";
  else if (ageH > warnHours) tone = "warn";

  const text = ageH < 1
    ? `${Math.round(ageMs / 60000)}m ago`
    : ageH < 48
      ? `${ageH.toFixed(1)}h ago`
      : `${(ageH / 24).toFixed(1)}d ago`;

  return (
    <Badge tone={tone} title={`last update: ${dt.toISOString()}`}>
      {label ? `${label}: ` : ""}{text}
    </Badge>
  );
}
